import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router, RouterLink, ActivatedRoute } from "@angular/router";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Observable, Subject } from "rxjs";

import moment from "moment";

import { CommonApiService } from "./../../../shared/services/api/common-api.service";
import { NotificationsService } from "./../../../shared/services/notifications.service";
import { ViewStateService } from "./../../../shared/services/view-state.service";

import { PayNowUser } from "../../shared/models/payments/pay-now-user";
import { IRenewal, Renewal } from "./../../shared/models/products/renewal";

import { PurchaseService } from "./../../shared/services/purchase.service";
import { PurchaseStateService } from "./../../shared/services/purchase-state.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-annual-renewal-paynow",
  templateUrl: "./annual-renewal-paynow.component.html",
  styleUrls: ["./annual-renewal-paynow.css"],
})
export class AnnualRenewalPayNowComponent implements OnInit, OnDestroy {
  renewalForm: FormGroup;
  product: IRenewal;
  isIneligible: boolean;
  isRenewalPending: boolean = true;
  renewalType: string;
  renewalInfo: any[];
  isSubjectToCMP: boolean;
  renewalYearCharity: any[];
  charities: any[];
  renewalsPicked: IRenewal[];
  source: string = "";
  user: PayNowUser;

  formLevelErrors: String[] = [];

  x = "";
  token = "";
  email = "";
  submitted = false;

  validFormCheck = true;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private purchaseService: PurchaseService,
    private purchaseState: PurchaseStateService,
    private viewState: ViewStateService,
    private router: Router,
    private commonApiService: CommonApiService,
    private notifications: NotificationsService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    let root = this.route.snapshot.root;
    this.token = root.queryParams["guid"];
    this.x = root.queryParams["x"];
    this.source = root.queryParams["source"];
    this.getProfileDetails(this.token, this.x);
    this.buildForm();
    this.renewalYearCharity = [];
  }

  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();

    if (!this.validateForm()) return;

    this.buildProductList();
    this.viewState.stopLoading();
    this.router.navigate(["practitioner/paynow/checkout"]);
  }
  cancel(event: Event) {
    event.preventDefault();
    this.renewalForm.reset();
    this.router.navigate(["/"]);
  }

  private getProfileDetails(guid: string, email: string) {
    this.purchaseService
      .getPayNow(guid, email, this.source)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (details) => {
          this.renewalInfo = details.renewal;
          this.source = details.source;
          this.user = new PayNowUser();
          this.user.email = details.email;
          this.user.personId = details.personId;
          this.user.fullName = details.fullName;
          this.user.firstName = details.firstName;
          this.user.lastName = details.lastName;
          this.user.middleName = details.middleName;
          this.user.line1 = details.line1;
          this.user.city = details.city;
          this.user.stateProvince = details.stateProvince;
          this.user.zip = details.zip;
          this.user.countryId = details.countryId;
          this.isSubjectToCMP = details.isSubjectToCMP;
          this.purchaseState.SetPersonForPayNow(this.user);

          if (this.renewalInfo) {
            this.renewalInfo.forEach((info: any) => {
              let renYear = info.renewalYear;
              this.isRenewalPending = false;
              this.getCharities(renYear);
            });
          }

          this.viewState.stopLoading();
        },
        (err) => {
          this.notifications.error(
            "Failed",
            "Record could not be located, please login to pay."
          );
          this.viewState.stopLoading();
          this.router.navigate(["/"]);
          console.log(err);
        }
      );
  }

  private validateForm() {
    if (!this.renewalsPicked || this.renewalsPicked.length == 0) {
      this.notifications.error("Selection Required", "Please select a year.");
      return false;
    }
    if (this.renewalsPicked) {
      for (var i in this.renewalsPicked) {
        let renYear: number = Number(this.renewalsPicked[i].renewalYear);
        if (
          renYear > 2019 &&
          renYear < 2023 &&
          !this.renewalsPicked[i].annualFeeCharityId
        ) {
          this.notifications.error(
            "Selection Required",
            "Please select an organization."
          );
          return false;
        }
      }
    }
    return true;
  }

  askCharities(renewalYear: number): boolean {
    return renewalYear > 2019;
  }

  private buildForm() {
    this.renewalForm = this.formBuilder.group({
      renewalType: [null],
    });
  }

  enableCharity(renewalYear) {
    if (this.renewalsPicked)
      return this.renewalsPicked.find((p) => p.renewalYear == renewalYear);
    else return false;
  }

  pickCharity(renewalYear, charity) {
    let idx: number = this.renewalsPicked.findIndex(
      (p) => p.renewalYear == renewalYear
    );
    this.renewalsPicked[idx].annualFeeCharityId = charity.id;
  }

  defaultNotParticipate(renewalYear) {
    let charityNdx = this.renewalYearCharity
      .find((p) => p.renewalYear == renewalYear)
      .charities.find((c) => c.description == "I choose not to participate").id;
    let idx: number = this.renewalsPicked.findIndex(
      (p) => p.renewalYear == renewalYear
    );
    this.renewalsPicked[idx].annualFeeCharityId = charityNdx;
  }

  chooseToParticipate(renewalYear, checked) {
    if (checked) {
      let charityNdx = this.renewalYearCharity
        .find((p) => p.renewalYear == renewalYear)
        .charities.find((c) => c.description == "I choose to give.").id;
      let idx: number = this.renewalsPicked.findIndex(
        (p) => p.renewalYear == renewalYear
      );
      this.renewalsPicked[idx].annualFeeCharityId = charityNdx;
    } else {
      this.defaultNotParticipate(renewalYear);
    }
  }

  getRenewalYearCharity(renewalYear) {
    if (this.renewalYearCharity)
      return this.renewalYearCharity.find((p) => p.renewalYear == renewalYear)
        .charities;
    else return null;
  }

  pickRenewal(renewal, checked) {
    let ren: IRenewal;
    ren = new Renewal();
    ren.renewalYear = renewal.renewalYear;
    ren.quantity = 1;
    ren.serviceCodeId = renewal.activeID;
    ren.price = renewal.cost;
    ren.status = "A"; //the API will pick the right one
    ren.source = this.source;

    if (!this.renewalsPicked && checked) {
      this.renewalsPicked = [];
      this.renewalsPicked.push(ren);
      if (parseInt(ren.renewalYear) > 2021 && parseInt(ren.renewalYear) < 2023)
        this.defaultNotParticipate(ren.renewalYear);
    } else if (
      checked &&
      !this.renewalsPicked.find((p) => p.renewalYear == renewal.renewalYear)
    ) {
      ren.renewalYear = renewal.renewalYear;
      this.renewalsPicked.push(ren);
      if (parseInt(ren.renewalYear) > 2021 && parseInt(ren.renewalYear) < 2023)
        this.defaultNotParticipate(ren.renewalYear);
    } else if (this.renewalsPicked && !checked) {
      this.renewalsPicked.splice(
        this.renewalsPicked.findIndex(
          (p) => p.renewalYear == renewal.renewalYear
        )
      );
    } else {
      //again not sure how we got here no renewals picked and unchecking
    }
  }

  private getCharities(renewalYear) {
    this.purchaseService.getCharities(renewalYear).subscribe((res) => {
      this.charities = res;
      var ren = { renewalYear: renewalYear, charities: this.charities };
      this.renewalYearCharity.push(ren);
    });
  }

  private buildProductList() {
    for (var i in this.renewalsPicked) {
      this.handleRenewalResponse(this.renewalsPicked[i]);
      this.purchaseState.patchProducts(this.product);
    }
  }

  private handleRenewalResponse(renewalInfo) {
    this.product = new Renewal();
    this.product.annualFeeCharityId = renewalInfo.annualFeeCharityId;
    this.product.renewalYear = renewalInfo.renewalYear;
    this.product.quantity = 1;
    this.product.title = "Annual Fee for ".concat(this.product.renewalYear);
    this.product.description = "Annual Fee for ".concat(
      this.product.renewalYear
    );
    this.product.price = renewalInfo.price;
    this.product.source = renewalInfo.source;

    if (this.isSubjectToCMP) {
      this.product.code = renewalInfo.serviceCodeId;
      this.product.status = "A";
      if (this.product.renewalYear == "2019") {
        this.product.code = "F04";
      } else {
        this.product.code = "F06";
      }
      this.product.serviceCodeId = renewalInfo.serviceCodeId;
    } else {
      this.product.code = renewalInfo.serviceCodeId;
      this.product.status = "S";
      if (this.product.renewalYear == "2019") {
        this.product.code = "F05";
      } else {
        this.product.code = "F07";
      }

      this.product.serviceCodeId = renewalInfo.serviceCodeId;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
